import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { Subscription } from "rxjs";
import { environment } from "src/environments/environment";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Area } from "src/model/class/general classes/area";
import {
  Building,
  InvitationBuilding,
} from "src/components/shared-components/class/general-classes/building";
import { User } from "src/model/class/general classes/user";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { currentBuildingUnsetAction } from "src/components/shared-components/state/actions/building.action";
import { AreaService } from "./area.service";
import { EventService } from "./event.service";
import { EnvironmentService } from "./environment.service";
import { EnvironmentCategory } from "src/components/shared-components/enum/enumEnvironment";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { filter, map } from "rxjs/operators";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { LoggerService } from "./logger.service";

@Injectable({
  providedIn: "root",
})
export class DpBuildingService implements iEvent {
  private _logContext: string = "Dp-building.service";
  _eventSubscriptions: Array<Subscription> = null;

  constructor(
    private _http: HttpClient,
    private _eventService: EventService,
    private _store: Store,
    private _areaService: AreaService,
    private _environmentService: EnvironmentService,
    private _loggerService: LoggerService,
  ) {
    this.subscribeEvents();
  }

  ngOnDestroy() {
    this.unsubscribeEvents();
  }

  /**
   * Update building
   * @param idBuilding
   * @param buildingUpdated
   * @returns
   */
  public updateBuildingbyID(
    idBuilding: number,
    buildingUpdated: Building,
  ): Promise<any> {
    return this._http
      .put(
        environment.httpDPAPIRoot +
          environment.anaciPlatformBuilding +
          AppSettings.API_URL_ANACI_BUILDING +
          "/" +
          idBuilding,
        buildingUpdated,
      )
      .toPromise();
  }

  public async deRegisterBuilding(exBuilding: Building) {
    try {
      await this.deleteBuildingbyID(exBuilding.id);

      const idAssociatedArea = exBuilding.idAreaLocal;

      this._areaService.deleteArea(idAssociatedArea);
    } catch (error) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "deRegisterBuilding()",
        "Exception: " + (error?.message ?? JSON.stringify(error)),
      );
      throw error;
    }
  }

  /**
   * Deleted building by id
   * @param idBuilding
   * @returns
   */
  public deleteBuildingbyID(idBuilding: number) {
    return this._http
      .delete(
        environment.httpDPAPIRoot +
          environment.anaciPlatformBuilding +
          AppSettings.API_URL_ANACI_BUILDING +
          "/" +
          idBuilding,
      )
      .toPromise();
  }

  /**
   * Get building by user
   * @param idUser
   * @returns
   */
  public getBuildingsbyIDUser(idUser): Promise<Array<Building>> {
    return this._http
      .get<
        Array<Building>
      >(environment.httpDPAPIRoot + environment.anaciPlatformBuilding + AppSettings.API_URL_ANACI_BUILDING + "?idUser=" + idUser)
      .toPromise();
  }

  /**
   * Get User by building
   */
  public getUsersByBuilding(idBuilding): Promise<Array<User>> {
    return this._http
      .get<
        Array<User>
      >(environment.httpDPAPIRoot + environment.anaciPlatformBuilding + AppSettings.API_URL_ANACI_BUILDING + "/" + idBuilding + "/listuser")
      .toPromise();
  }

  /**
   * Get administrator by building
   */
  public getAdministratorByBuilding(idBuilding): Promise<Array<User>> {
    return this._http
      .get<
        Array<User>
      >(environment.httpDPAPIRoot + environment.anaciPlatformBuilding + AppSettings.API_URL_ANACI_BUILDING + "/" + idBuilding + "/administrator")
      .toPromise();
  }

  /**
   * Handle complete process of creation and registration of new Building.
   * @param buildingUpdated
   * @returns
   */
  public async registerNewBuilding(
    buildingUpdated: Building,
    currentUser: CurrentSessionData,
  ): Promise<Building> {
    try {
      const newArea = new Area();
      newArea.instanceGUID = AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE;
      newArea.customDescription = buildingUpdated.name;
      newArea.description = buildingUpdated.name;

      const associatedArea: Area = await this._areaService
        .insertArea(newArea, -1)
        .toPromise();
      buildingUpdated.idAreaLocal = associatedArea.id;

      //If the environment is linked to the building, it is necessary to set removable to false, so the environment cannot be removed
      let removable = false;
      let environmentInserted = await this._environmentService
        .insertCondoEnvironment(
          AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE,
          associatedArea.id,
          EnvironmentCategory.CONDOMINIUM,
          newArea.customDescription,
          removable,
        )
        .toPromise();

      await this._environmentService.addedPermission(
        currentUser,
        environmentInserted.id,
      );

      const newBuilding = await this.insertBuilding(buildingUpdated);

      return newBuilding;
    } catch (errror) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "registerNewBuilding()",
        "Exception: " + errror.message,
      );
      throw errror;
    }
  }

  /**
   * Insert building in DIGITALPLACE-specific DB.
   * @param buildingUpdated
   * @returns
   */
  private insertBuilding(buildingUpdated: Building) {
    return this._http
      .post<Building>(
        environment.httpDPAPIRoot +
          environment.anaciPlatformBuilding +
          AppSettings.API_URL_ANACI_BUILDING,
        buildingUpdated,
      )
      .toPromise();
  }

  public deselectCurrentBuilding(): void {
    this._store.dispatch(currentBuildingUnsetAction());
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(() => {
        this.deselectCurrentBuilding();
      }),
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }

  //#endregion
}
